import axios from "axios";

// Configurar la base URL del API (backend propio)
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Rutas de los procesadores de IA — el proxy de CRA las reenvía a core-api-dev.sdelsol.com
const AI_TEAMSYSTEM_URL = `${API_BASE_URL}${process.env.REACT_APP_AI_TEAMSYSTEM_URL}`;
const AI_GEMINI_URL = `${API_BASE_URL}${process.env.REACT_APP_AI_GEMINI_URL}`;

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: "",
  timeout: 30000, // 30 segundos timeout
  headers: {
    "Content-Type": "application/json"
  }
});

// Interceptor para agregar el token de autenticación a las peticiones
apiClient.interceptors.request.use(
  (config) => {
    // Obtener el token de SessionStorage
    const token = sessionStorage.getItem("token_TSE");

    // Si existe el token, agregarlo al header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error);

    // Si el error es 401 (No autorizado), limpiar sesión y redirigir al login
    if (error.response && error.response.status === 401) {
      sessionStorage.clear();
      window.location.href = "/login";
      throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
    }

    if (error.code === "ECONNABORTED") {
      throw new Error("Tiempo de espera agotado. El servidor tardó demasiado en responder.");
    }

    if (error.response) {
      // El servidor respondió con un estado de error
      const message =
        error.response.data?.message || error.response.data?.error || `Error del servidor: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      console.log(error);
      // La solicitud se envió pero no hubo respuesta
      throw new Error("No se pudo conectar con el servidor. Verifica tu conexión.");
    } else {
      // Error en la configuración de la solicitud
      throw new Error("Error al procesar la solicitud");
    }
  }
);

// Servicio para análisis con IA
export const aiService = {
  /**
   * Envía archivo en base64 para análisis con IA
   * @param {Object} payload - Datos para análisis
   * @param {string} payload.file - Archivo en formato data URI base64
   * @param {Object} payload.schema - Schema JSON opcional
   * @param {string} payload.fineTune - Parámetros de fine-tuning opcionales
   * @returns {Promise} Resultado del análisis
   */
  analyzeContent: async (payload, processorType) => {
    let url;
    if (processorType === "ts" || processorType === "teamsystem") {
      url = AI_TEAMSYSTEM_URL;
    } else if (processorType === "gemini") {
      url = AI_GEMINI_URL;
    }

    const token = sessionStorage.getItem("token_TSE");

    const response = await apiClient.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
        ...(token && {Authorization: `Bearer ${token}`})
      }
    });

    return response.data;
  },

  /**
   * Verifica el estado de salud del servidor
   * @returns {Promise} Estado del servidor
   */
  checkHealth: async () => {
    const response = await apiClient.get("/health");
    return response.data;
  },

  /**
   * Verifica si el servidor está listo
   * @returns {Promise} Estado de readiness del servidor
   */
  checkReadiness: async () => {
    const response = await apiClient.get("/ready");
    return response.data;
  }
};

// Servicio de utilidades
export const utilsService = {
  /**
   * Convierte un archivo a base64
   * @param {File} file - Archivo a convertir
   * @returns {Promise<string>} Archivo en formato base64
   */
  fileToBase64: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  },

  /**
   * Valida si un archivo es una imagen
   * @param {File} file - Archivo a validar
   * @returns {boolean} True si es una imagen
   */
  isImageFile: (file) => {
    return file.type.startsWith("image/");
  },

  /**
   * Valida si un archivo es un PDF
   * @param {File} file - Archivo a validar
   * @returns {boolean} True si es un PDF
   */
  isPDFFile: (file) => {
    return file.type === "application/pdf";
  },

  /**
   * Valida el tamaño de un archivo
   * @param {File} file - Archivo a validar
   * @param {number} maxSizeInMB - Tamaño máximo en MB
   * @returns {boolean} True si el tamaño es válido
   */
  validateFileSize: (file, maxSizeInMB = 10) => {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  },

  /**
   * Formatea el tamaño de archivo en formato legible
   * @param {number} bytes - Tamaño en bytes
   * @returns {string} Tamaño formateado
   */
  formatFileSize: (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
};

export default apiClient;
